/**
 * localStorage persistence for user column preferences (order + visibility),
 * keyed by table type. Sibling of `columnWidthStorage.ts` — same silent
 * try/catch helper pattern.
 *
 * Key scheme: `editable-table:${storageKey}:column-prefs` →
 * `{ order?: string[], hidden?: string[] }` of `col.data` keys.
 *
 * The module stores raw user intent and knows nothing about column
 * defaults — reconciliation against the live column set (dropping removed
 * columns, inserting new ones, default-hidden seeding) lives in
 * `useColumnPrefs`. Stale entries for columns that no longer exist are
 * harmless — the hook simply ignores unknown keys.
 */

const keyFor = (storageKey: string) => `editable-table:${storageKey}:column-prefs`;

export interface ColumnPrefs {
  /** Visible + hidden column `data` keys in the user's order. Absent = default order. */
  order?: string[];
  /** Column `data` keys the user hid. Absent = the columns' `defaultHidden` flags apply. */
  hidden?: string[];
}

/** Keep only deduped string entries; `undefined` when not a usable array. */
function sanitizeKeyList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const keys = value.filter((entry): entry is string => typeof entry === 'string');
  return [...new Set(keys)];
}

/**
 * Read persisted column prefs. Returns `{}` when missing or corrupt.
 * Each field is validated independently so a malformed `order` doesn't
 * discard a valid `hidden` (and vice versa).
 */
export function getStoredColumnPrefs(storageKey: string): ColumnPrefs {
  try {
    const raw = localStorage.getItem(keyFor(storageKey));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const prefs: ColumnPrefs = {};
    const order = sanitizeKeyList((parsed as Record<string, unknown>).order);
    const hidden = sanitizeKeyList((parsed as Record<string, unknown>).hidden);
    if (order !== undefined) prefs.order = order;
    if (hidden !== undefined) prefs.hidden = hidden;
    return prefs;
  } catch {
    // corrupt data – ignore
    return {};
  }
}

/**
 * Write column prefs. Callers omit a field when it matches the column
 * defaults (see `useColumnPrefs`); when both fields are absent the key is
 * removed entirely so an untouched table leaves no residue.
 */
export function setStoredColumnPrefs(storageKey: string, prefs: ColumnPrefs): void {
  try {
    const payload: ColumnPrefs = {};
    if (prefs.order !== undefined) payload.order = prefs.order;
    if (prefs.hidden !== undefined) payload.hidden = prefs.hidden;
    if (payload.order === undefined && payload.hidden === undefined) {
      localStorage.removeItem(keyFor(storageKey));
    } else {
      localStorage.setItem(keyFor(storageKey), JSON.stringify(payload));
    }
  } catch {
    // storage full or unavailable – ignore
  }
}
